/**
 *
 *
 * NOTE
 * A lot of the calcs below are mirrored at the backend for display on the order PDFs.
 *  Ideally this wouldn't be the case, but for various reasons this is the best solution for now.
 *  This is something we should look at consolidating in the future if we get the chance.
 *
 *  Also, any changes below, sure the same change is mirrored in the backend.
 *  Calc-type-specific logic lives in helpers/net-cost/ modules - mirror changes there too.
 *
 *
 *
 */

import * as BikPlusFixedTradeup from './net-cost/bik-plus-fixed-tradeup';
import * as BikPlusNetImpact from './net-cost/bik-plus-net-impact';

/**
 * Dispatch map for per-calc-type netCost() implementations.
 * Keys match calcs_net_cost_display values; values are functions(ratebook, term, context).
 */
const calcDispatch = {
    bikPlusFixedTradeup: (ratebook, term, context) => BikPlusFixedTradeup.netCost(ratebook, term, context),
    bikPlusNetImpact:    (ratebook, term, context) => BikPlusNetImpact.netCost(ratebook, term, context),
};

export default {
    defaultNetCostCalcMethod: "netPresentValue",

    /**
     * Returns true when the driver grade name marks the grade as ratebook-enabled.
     * Guarded against a missing name, which would otherwise throw and blank the whole breakdown.
     *
     * @param {string|null} driverGradeName
     * @returns {boolean}
     */
    isRatebookEnabledGrade: function (driverGradeName) {
        return !!driverGradeName && driverGradeName.toLowerCase().includes("bch+");
    },

    /** comparison_bch_bik_display values (BCH product, non-BCH+ grades only). */
    BIK_DISPLAY_JAAMA_AVG: "jaama_avg",
    BIK_DISPLAY_DELOITTE_AVG_BREAKDOWN: "deloitte_avg_breakdown",

    /**
     * Resolves the configured non-BCH+ BiK display mode. Anything other than the Deloitte mode
     * (missing, null, empty, unrecognised, or a legacy "none") resolves to the Jaama average, i.e. the
     * existing "Monthly BiK" display.
     *
     * @param {object} adminPortalProduct - The BCH product config object.
     * @returns {string} One of BIK_DISPLAY_JAAMA_AVG | BIK_DISPLAY_DELOITTE_AVG_BREAKDOWN.
     */
    bikDisplayMode: function (adminPortalProduct) {
        return adminPortalProduct?.comparison_bch_bik_display === this.BIK_DISPLAY_DELOITTE_AVG_BREAKDOWN
            ? this.BIK_DISPLAY_DELOITTE_AVG_BREAKDOWN
            : this.BIK_DISPLAY_JAAMA_AVG;
    },

    /**
     * Returns true when the BCH product is configured to show the Deloitte average breakdown display.
     *
     * @param {object} adminPortalProduct - The BCH product config object.
     * @returns {boolean}
     */
    isDeloitteAvgBreakdownBikDisplay: function (adminPortalProduct) {
        return this.bikDisplayMode(adminPortalProduct) === this.BIK_DISPLAY_DELOITTE_AVG_BREAKDOWN;
    },

    /**
     * Calculate the estimated net monthly cost.
     *
     * @param {object} ratebook
     * @param {number} term
     * @param {string|null} calcMethod  - calcs_net_cost_display value
     * @param {{ ratePercent?: number, driverGradeName?: string, driverGrade?: object }} [context={}]
     *   Required for calc-type branches; backward-compatible (existing callers pass nothing).
     *   driverGradeName feeds bikPlusFixedTradeup; driverGrade (quote.driverGrade) feeds bikPlusNetImpact.
     * @returns {string|null}
     */
    netCost: function (ratebook, term, calcMethod, context = {}) {
        let formula = calcMethod || this.defaultNetCostCalcMethod;
        // Default: netPresentValue
        let cost = Number(ratebook?.employeeCostCompanyCar?.totals_NetPresentValueNPV / Number(term));

        if (formula === 'bikPlusContributions') {
            cost = Number(ratebook.bikTaxAverage) + Number(ratebook?.employeeCostCompanyCar?.motoringAndFinance_PrivateUseContributions) + Number(ratebook?.employeeCostCompanyCar?.motoringAndFinance_CapitalContributions);
        }

        // Dispatch to per-calc module
        if (calcDispatch[formula]) {
            cost = calcDispatch[formula](ratebook, term, context);
        }

        return cost ? cost.toFixed(2) : null;
    },
    netCostPounds(ratebook, term, calcMethod, context = {}) {
        if (!this.netCost(ratebook, term, calcMethod, context)) {
            return null;
        }

        const parts = this.netCost(ratebook, term, calcMethod, context).split(".");
        return parts[0];
    },
    netCostPence(ratebook, term, calcMethod, context = {}) {
        if (!this.netCost(ratebook, term, calcMethod, context)) {
            return null;
        }
        const parts = this.netCost(ratebook, term, calcMethod, context).split(".");
        return parts[1];
    },
    oneOffContribution: function (ratebook, term) {
        const cost =
            ratebook?.employeeCostCompanyCar
                ?.motoringAndFinance_CapitalContributions;
        return cost ? Number(cost) : 0;
    },
    vehicleRental: function (ratebook, term) {
        return ratebook?.companyCostCompanyCar
            ?.motoringAndFinance_RentalsPayments
            ? Number(
                  ratebook?.companyCostCompanyCar
                      ?.motoringAndFinance_RentalsPayments / Number(term),
              ).toFixed(2)
            : null; // Replace to come from jaama
    },
    tradeUpDownRawValue: function (ratebook) {
        return ratebook?.employeeCostCompanyCar
            ?.motoringAndFinance_PrivateUseContributions;
    },
    tradeUpDown: function (ratebook, term) {
        return this.tradeUpDownRawValue(ratebook)
            ? `${(Number(this.tradeUpDownRawValue(ratebook)) / Number(term)).toFixed(2)}`
            : null;
    },
    /** Unrounded monthly whole life cost, or null when unavailable. */
    wholeLifeCostRaw: function (ratebook, term) {
        const total = ratebook?.companyCostCompanyCar?.totals_Total;
        return total ? Number(total) / Number(term) : null;
    },
    wholeLifeCost: function (ratebook, term) {
        const raw = this.wholeLifeCostRaw(ratebook, term);
        return raw !== null ? raw.toFixed(2) : null;
    },
    /** Unrounded average monthly BiK, or null when unavailable. */
    averageMonthlyBikRaw: function (ratebook, term) {
        const cost =
            ratebook?.employeeCostCompanyCar?.carBenefit_IncomeTaxOnCarBenefit;
        return cost ? Number(cost) / Number(term) : null;
    },
    averageMonthlyBik: function (ratebook, term) {
        const raw = this.averageMonthlyBikRaw(ratebook, term);
        return raw !== null ? raw.toFixed(2) : null;
    },
    bikAmounts: function (ratebook) {
        return ratebook?.bikMonthlyAmounts
            ? ratebook?.bikMonthlyAmounts.map((amount) => {
                  return {
                      year: amount.taxYearEnd,
                      amount: Number(amount.amount).toFixed(2),
                  };
              })
            : [];
    },
    oneOffContributionRemaining: function (
        ratebook,
        maxMonthlyContribution,
        term,
    ) {
        const totalContribution = this.oneOffContribution(ratebook, term);
        return totalContribution && maxMonthlyContribution
            ? Number(maxMonthlyContribution) * term - Number(totalContribution)
            : null;
    },
    wholeLifeCostRemaining: function (ratebook, maxWholeLifeCost, term) {
        const selectedWholeLifeCost = this.wholeLifeCost(ratebook, term);
        return maxWholeLifeCost && selectedWholeLifeCost
            ? Number(maxWholeLifeCost) - Number(selectedWholeLifeCost)
            : null;
    },

    // Check config for what we should/shouldnt show
    showWholeLifeCosts: function (adminPortalProduct) {
        return adminPortalProduct?.your_shortlist_display_estimated_whole_life_cost;
    },
    showVehicleRental: function (adminPortalProduct) {
        return adminPortalProduct?.your_shortlist_display_vehicle_rental_cost;
    },
};
